// Guest flow: email -> verify -> details -> request form -> confirmation.
// One route, one component owning the step machine — kept as a single
// stateful page rather than five routes because the whole flow is a single
// linear session with tight cross-step dependencies (the email captured in
// step 1 must thread unchanged into the final submit; splitting this into
// separate routes would just move that same state up into a shared
// ancestor anyway, with more code, not less — portal/CLAUDE.md also
// explicitly rules out "route-level code splitting theatre" for this app's
// five-ish screens).
//
// Resilience (portal/CLAUDE.md): guest email, verified-at timestamp,
// details draft, and lines draft are each mirrored to localStorage via
// usePersistedState as the user types, so a reload mid-session doesn't
// erase progress. Cleared only on a successful final submit. On mount, if a
// verified receipt is still fresh (< OTP_RECEIPT_WINDOW_MINUTES old,
// mirroring backend/src/services/guestService.js's own window), the flow
// resumes at the 'details' step directly — skipping back through the OTP
// step on every reload would be a genuinely bad experience for exactly the
// interrupted-session user this app is built for.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import apiClient, { ApiError } from 'shared/api/client.js';
import PortalShell from '../components/PortalShell.jsx';
import GuestEmailStep from '../components/GuestEmailStep.jsx';
import GuestVerifyStep from '../components/GuestVerifyStep.jsx';
import GuestDetailsStep from '../components/GuestDetailsStep.jsx';
import RequestLinesForm from '../components/RequestLinesForm.jsx';
import RequestConfirmation from '../components/RequestConfirmation.jsx';
import usePersistedState from '../hooks/usePersistedState.js';
import { mergePendingCatalogItems } from '../utils/pendingLine.js';
import { mapApiErrorId } from '../utils/errorMapping.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';

const EMAIL_KEY = 'sneethel.portal.guest.email';
const VERIFIED_AT_KEY = 'sneethel.portal.guest.verifiedAt';
const DETAILS_KEY = 'sneethel.portal.guest.details';
const LINES_KEY = 'sneethel.portal.guest.lines';

// Mirrors backend/src/services/guestService.js's OTP_RECEIPT_WINDOW_MINUTES
// (30) — a client-side estimate only, used purely to decide which step to
// resume on. The server is the real authority: if this guess is wrong (e.g.
// the two clocks disagree), the final submit still re-checks server-side
// and OTP_NOT_VERIFIED sends the user back to request a fresh code exactly
// as if this guess had been correct from the start.
const RECEIPT_WINDOW_MS = 30 * 60 * 1000;

async function requestOtp(destination) {
  return apiClient.post('/guest/otp/request', { destination });
}

async function verifyOtp({ destination, code }) {
  return apiClient.post('/guest/otp/verify', { destination, code });
}

// Same JSON-or-multipart branch as pages/RequestFormPage.jsx's own
// buildRequestBody — see that file's comment. Guest submissions carry more
// fields (tin/organizationName/contactName/... alongside neededBy/purpose/
// buyerNote/lines/file), so every string field gets the same "append if
// present" treatment here.
function buildGuestRequestBody({
  tin,
  organizationName,
  contactName,
  contactEmail,
  contactPhone,
  address,
  neededBy,
  purpose,
  buyerNote,
  lines,
  file,
}) {
  if (!file) {
    return {
      tin,
      organizationName,
      contactName,
      contactEmail,
      contactPhone,
      address,
      neededBy,
      purpose,
      buyerNote,
      lines,
    };
  }
  const formData = new FormData();
  const stringFields = {
    tin,
    organizationName,
    contactName,
    contactEmail,
    contactPhone,
    address,
    neededBy,
    purpose,
    buyerNote,
  };
  Object.entries(stringFields).forEach(([key, value]) => {
    if (value) formData.append(key, value);
  });
  formData.append('lines', JSON.stringify(lines));
  formData.append('file', file);
  return formData;
}

async function submitGuestRequest(body) {
  return apiClient.post('/guest/requests', buildGuestRequestBody(body));
}

export default function GuestPage() {
  const navigate = useNavigate();
  const [email, setEmail, clearEmail] = usePersistedState(EMAIL_KEY, '');
  const [verifiedAt, setVerifiedAt, clearVerifiedAt] = usePersistedState(VERIFIED_AT_KEY, null);
  const [details, setDetails, clearDetails] = usePersistedState(DETAILS_KEY, null);
  const [linesDraft, setLinesDraft, clearLinesDraft] = usePersistedState(
    LINES_KEY,
    null,
    mergePendingCatalogItems
  );

  const isVerifiedFresh = Boolean(
    email && verifiedAt && Date.now() - verifiedAt < RECEIPT_WINDOW_MS
  );

  const [step, setStep] = useState(() => (isVerifiedFresh ? 'details' : 'email'));
  const [emailNoticeId, setEmailNoticeId] = useState(null);
  const [resendNoticeShown, setResendNoticeShown] = useState(false);
  const [refNo, setRefNo] = useState(null);
  const [trackingToken, setTrackingToken] = useState(null);

  const requestOtpMutation = useMutation({ mutationFn: requestOtp });
  const verifyOtpMutation = useMutation({ mutationFn: verifyOtp });
  const submitMutation = useMutation({ mutationFn: submitGuestRequest });
  const { t, toastOk, toastErr } = useActionFeedback();

  const handleEmailSubmit = (submittedEmail) => {
    setEmailNoticeId(null);
    setEmail(submittedEmail);
    // A new email always starts a fresh verification.
    clearVerifiedAt();
    requestOtpMutation.mutate(submittedEmail, {
      onSuccess: () => {
        setStep('verify');
        toastOk(t('portal.guest.otpSentToast', { email: submittedEmail }));
      },
      onError: (err) => toastErr(err),
    });
  };

  const handleVerifySubmit = (code) => {
    verifyOtpMutation.mutate(
      { destination: email, code },
      {
        onSuccess: () => {
          setVerifiedAt(Date.now());
          setStep('details');
          toastOk(t('portal.guest.otpVerifiedToast'));
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'OTP_TOO_MANY_ATTEMPTS') {
            setEmailNoticeId('error.OTP_TOO_MANY_ATTEMPTS');
            setStep('email');
          } else if (err instanceof ApiError && err.code === 'OTP_NOT_REQUESTED') {
            setEmailNoticeId('error.OTP_NOT_REQUESTED');
            setStep('email');
          }
          toastErr(err, ['OTP_INCORRECT', 'OTP_TOO_MANY_ATTEMPTS', 'OTP_NOT_REQUESTED']);
        },
      }
    );
  };

  const handleResend = () => {
    setResendNoticeShown(false);
    requestOtpMutation.mutate(email, {
      onSuccess: () => {
        setResendNoticeShown(true);
        toastOk(t('portal.guest.otpResentToast'));
      },
      onError: (err) => toastErr(err),
    });
  };

  const handleChangeEmail = () => {
    clearEmail();
    clearVerifiedAt();
    setStep('email');
  };

  const handleDetailsContinue = (values) => {
    setDetails(values);
    setStep('form');
  };

  const handleRequestSubmit = (lineValues) => {
    submitMutation.mutate(
      {
        ...details,
        contactEmail: email,
        neededBy: lineValues.neededBy,
        purpose: lineValues.purpose,
        buyerNote: lineValues.buyerNote,
        lines: lineValues.lines,
        file: lineValues.file,
      },
      {
        onSuccess: ({ request }) => {
          clearEmail();
          clearVerifiedAt();
          clearDetails();
          clearLinesDraft();
          setRefNo(request.ref_no);
          setTrackingToken(request.trackingToken || null);
          setStep('confirmation');
          toastOk(t('portal.requestForm.submittedToast', { refNo: request.ref_no }));
        },
        onError: (err) => {
          if (err instanceof ApiError && err.code === 'OTP_NOT_VERIFIED') {
            // Slow/interrupted session — keep the drafts, reset only the
            // verification step.
            clearVerifiedAt();
            setEmailNoticeId('error.OTP_NOT_VERIFIED');
            setStep('email');
          }
          toastErr(err, [
            'NO_ORGANIZATION',
            'EMPTY_REQUEST',
            'VALIDATION_ERROR',
            'OTP_NOT_VERIFIED',
          ]);
        },
      }
    );
  };

  const verifyErrorId = verifyOtpMutation.error
    ? mapApiErrorId(verifyOtpMutation.error, ['OTP_INCORRECT'])
    : null;
  // POST /guest/otp/request never returns a business error (it always
  // responds { sent: true } — see backend/src/routes/guestRoutes.js) — the
  // only thing that can land here is a genuine network/server failure, so
  // no code is ever in the known-allow-list, mapApiErrorId always falls
  // through to NETWORK_ERROR/INTERNAL_ERROR.
  const requestOtpErrorId = requestOtpMutation.error
    ? mapApiErrorId(requestOtpMutation.error, [])
    : null;

  const linesDefaultValues = useMemo(
    () => linesDraft || { neededBy: '', purpose: '', buyerNote: '', lines: [] },
    [linesDraft]
  );

  const handleNewRequest = () => {
    setRefNo(null);
    setTrackingToken(null);
    setStep('email');
  };

  return (
    <PortalShell
      backTo={step === 'email' ? '/' : undefined}
      width={step === 'form' ? 'wide' : step === 'details' ? 'medium' : 'narrow'}
    >
      {step === 'email' && (
        <GuestEmailStep
          defaultEmail={email}
          onSubmit={handleEmailSubmit}
          isSubmitting={requestOtpMutation.isPending}
          noticeId={emailNoticeId}
          submitErrorId={requestOtpErrorId}
          submitError={requestOtpMutation.error}
        />
      )}
      {step === 'verify' && (
        <GuestVerifyStep
          email={email}
          onSubmit={handleVerifySubmit}
          isSubmitting={verifyOtpMutation.isPending}
          submitErrorId={verifyErrorId}
          submitError={verifyOtpMutation.error}
          onResend={handleResend}
          isResending={requestOtpMutation.isPending}
          resendErrorId={requestOtpErrorId}
          resendError={requestOtpMutation.error}
          resendNoticeShown={resendNoticeShown}
          onChangeEmail={handleChangeEmail}
        />
      )}
      {step === 'details' && (
        <GuestDetailsStep
          email={email}
          defaultValues={details}
          onDraftChange={setDetails}
          onContinue={handleDetailsContinue}
        />
      )}
      {step === 'form' && (
        <RequestLinesForm
          defaultValues={linesDefaultValues}
          onDraftChange={setLinesDraft}
          onSubmit={handleRequestSubmit}
          isSubmitting={submitMutation.isPending}
        />
      )}
      {step === 'confirmation' && (
        <RequestConfirmation
          refNo={refNo}
          trackingToken={trackingToken}
          onNewRequest={() => {
            handleNewRequest();
            navigate('/start');
          }}
        />
      )}
    </PortalShell>
  );
}
